/**
 * ContributorDetail Component
 * Shows full details for a selected contributor with actions
 */

import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Chip,
  Button,
  Divider,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Tabs,
  Tab
} from '@mui/material';
import {
  Person as PersonIcon,
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  Block as DisableIcon,
  PlayArrow as ActivateIcon,
  Public as PublicIcon,
  VisibilityOff as PrivateIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import {
  useContributor,
  useApprovePublicListing,
  useRejectPublicListing,
  useUpdateContributor
} from '../../hooks/useContributors';

const STATUS_CONFIG = {
  activated: { label: 'Activated', color: 'success' },
  pending: { label: 'Pending', color: 'warning' },
  disabled: { label: 'Disabled', color: 'error' },
  deleted: { label: 'Deleted', color: 'default' }
};

export const ContributorDetail = ({ contributorId, onActionComplete }) => {
  const navigate = useNavigate();
  const [tabValue, setTabValue] = useState(0);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [error, setError] = useState('');

  // Fetch contributor details
  const { data: contributor, isLoading, error: fetchError } = useContributor(contributorId);

  // Mutations
  const approveMutation = useApprovePublicListing();
  const rejectMutation = useRejectPublicListing();
  const updateMutation = useUpdateContributor();

  if (!contributorId) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography color="text.secondary">
          Select a contributor to view details
        </Typography>
      </Box>
    );
  }

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (fetchError) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="error">
          Error loading contributor: {fetchError.message}
        </Alert>
      </Box>
    );
  }

  if (!contributor) {
    return null;
  }

  const statusConfig = STATUS_CONFIG[contributor.status] || STATUS_CONFIG.pending;
  const needsApproval = contributor.wantsPublicListing && !contributor.isPubliclyVisible;
  const canApprove = contributor.status === 'activated' && needsApproval;
  const canToggleStatus = contributor.status !== 'deleted' && contributor.status !== 'pending';

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  const handleApprove = async () => {
    setError('');
    try {
      await approveMutation.mutateAsync(contributorId);
      onActionComplete?.();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to approve');
    }
  };

  const handleReject = async () => {
    setError('');
    try {
      await rejectMutation.mutateAsync({ id: contributorId, reason: rejectReason });
      setRejectDialogOpen(false);
      setRejectReason('');
      onActionComplete?.();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to reject');
    }
  };

  const handleToggleStatus = async () => {
    setError('');
    const newStatus = contributor.status === 'activated' ? 'disabled' : 'activated';
    try {
      await updateMutation.mutateAsync({
        id: contributorId,
        data: { status: newStatus }
      });
      onActionComplete?.();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update status');
    }
  };

  const isActionLoading = approveMutation.isLoading || rejectMutation.isLoading || updateMutation.isLoading;

  return (
    <Box sx={{ height: '100%', overflow: 'auto', p: 2 }}>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Header */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <PersonIcon sx={{ fontSize: 48, color: 'primary.main' }} />
          <Box sx={{ flex: 1 }}>
            <Typography variant="h5">{contributor.handle}</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
              <Chip
                label={statusConfig.label}
                color={statusConfig.color}
                size="small"
              />
              {contributor.isPubliclyVisible ? (
                <Chip
                  icon={<PublicIcon />}
                  label="Publicly Visible"
                  color="success"
                  size="small"
                  variant="outlined"
                />
              ) : contributor.wantsPublicListing ? (
                <Chip
                  icon={<PrivateIcon />}
                  label="Awaiting Approval"
                  color="warning"
                  size="small"
                />
              ) : (
                <Chip
                  icon={<PrivateIcon />}
                  label="Private"
                  size="small"
                  variant="outlined"
                />
              )}
            </Box>
          </Box>
        </Box>

        {/* Action Buttons */}
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {canApprove && (
            <>
              <Button
                variant="contained"
                color="success"
                startIcon={<ApproveIcon />}
                onClick={handleApprove}
                disabled={isActionLoading}
              >
                Approve Public Listing
              </Button>
              <Button
                variant="outlined"
                color="error"
                startIcon={<RejectIcon />}
                onClick={() => setRejectDialogOpen(true)}
                disabled={isActionLoading}
              >
                Reject
              </Button>
            </>
          )}

          {canToggleStatus && (
            <Button
              variant="outlined"
              color={contributor.status === 'activated' ? 'error' : 'success'}
              startIcon={contributor.status === 'activated' ? <DisableIcon /> : <ActivateIcon />}
              onClick={handleToggleStatus}
              disabled={isActionLoading}
            >
              {contributor.status === 'activated' ? 'Disable Account' : 'Activate Account'}
            </Button>
          )}
        </Box>
      </Paper>

      {/* Details */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="h6" gutterBottom>Account Details</Typography>
        <Divider sx={{ mb: 2 }} />

        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
          <Box>
            <Typography variant="caption" color="text.secondary">Email</Typography>
            <Typography>{contributor.contact_email || 'Not provided'}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">Email Confirmed</Typography>
            <Typography>{contributor.email_confirmed ? 'Yes' : 'No'}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">Created</Typography>
            <Typography>{formatDate(contributor.createdAt)}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">Last Receipt Validation</Typography>
            <Typography>{formatDate(contributor.last_receipt_validation)}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">Release Agreement</Typography>
            <Typography>
              {contributor.release_agreement_accepted_at
                ? `Accepted ${formatDate(contributor.release_agreement_accepted_at)}`
                : 'Not accepted'}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">Wants Public Listing</Typography>
            <Typography>{contributor.wantsPublicListing ? 'Yes' : 'No'}</Typography>
          </Box>
        </Box>

        {contributor.bio && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="caption" color="text.secondary">Bio</Typography>
            <Typography sx={{ whiteSpace: 'pre-wrap' }}>{contributor.bio}</Typography>
          </Box>
        )}
      </Paper>

      {/* Submissions & Games Tabs */}
      <Paper sx={{ mb: 2 }}>
        <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)}>
          <Tab label={`Submissions (${contributor.submissions?.length || 0})`} />
          <Tab label={`Published Games (${contributor.published_games?.length || 0})`} />
        </Tabs>

        <Box sx={{ p: 2 }}>
          {tabValue === 0 && (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Game Code</TableCell>
                    <TableCell>Language</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Submitted</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {!contributor.submissions?.length ? (
                    <TableRow>
                      <TableCell colSpan={4} align="center">
                        <Typography color="text.secondary">No submissions</Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    contributor.submissions.map((sub) => (
                      <TableRow
                        key={sub.id}
                        hover
                        sx={{ cursor: 'pointer' }}
                        onClick={() => navigate(`/submissions?id=${sub.id}`)}
                      >
                        <TableCell>{sub.gameCode}</TableCell>
                        <TableCell>{sub.language?.toUpperCase()}</TableCell>
                        <TableCell>
                          <Chip label={sub.status} size="small" />
                        </TableCell>
                        <TableCell>{formatDate(sub.submittedAt)}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {tabValue === 1 && (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Game Code</TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell>Language</TableCell>
                    <TableCell>Channel</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {!contributor.published_games?.length ? (
                    <TableRow>
                      <TableCell colSpan={4} align="center">
                        <Typography color="text.secondary">No published games</Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    contributor.published_games.map((game) => (
                      <TableRow key={game.id} hover>
                        <TableCell>{game.gameCode}</TableCell>
                        <TableCell>{game.gameName}</TableCell>
                        <TableCell>{game.language?.toUpperCase()}</TableCell>
                        <TableCell>
                          <Chip label={game.releaseChannel} size="small" />
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      </Paper>

      {/* Reject Dialog */}
      <Dialog open={rejectDialogOpen} onClose={() => setRejectDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Reject Public Listing Request</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            This will reject {contributor.handle}'s request to be listed on the public contributors page.
          </Typography>
          <TextField
            label="Reason (optional)"
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            multiline
            rows={3}
            fullWidth
            placeholder="Provide a reason for the rejection..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleReject}
            variant="contained"
            color="error"
            disabled={rejectMutation.isLoading}
          >
            Reject
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
